import React, {
    Fragment,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import NextArrow from "./Arrows/nextArrow";
import PrevArrow from "./Arrows/prevArrow";
import "swiper/css";
import "swiper/css/navigation";
import Common from "../../utils/common";
import Dotdotdot from "react-dotdotdot";
import "./ChannelSliderItem.scss";

const ChannelSliderItem = ({ channel }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);
    const containerRef = useRef(null);
    const swiperRef = useRef(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);

    const setArrow = useCallback(() => {
        const parent = containerRef.current;
        if (!parent) return;
        const thumb = parent.querySelector("div.tvp-video-item-thumbnail");
        const arrows = parent.querySelectorAll(
            ".tvp-carousel-arrow.prev, .tvp-carousel-arrow.next"
        );
        if (thumb && arrows.length) {
            arrows.forEach((arrow) => {
                arrow.style.top = `${
                    parent.offsetHeight / 2 - arrow.offsetHeight / 2
                }px`;
            });
        }
    }, []);

    useEffect(() => {
        window.addEventListener("resize", setArrow);
        setArrow();
        return () => {
            window.removeEventListener("resize", setArrow);
        };
    }, [setArrow]);

    const onSwiper = (swiper) => {
        swiperRef.current = swiper;
        // ensure DOM refs are mounted
        setTimeout(() => {
            if (!swiper.params.navigation) return;
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.destroy();
            swiper.navigation.init();
            swiper.navigation.update();
            // reposition arrows after init
            const parent = containerRef.current;
            if (!parent) return;
            setArrow();
        });
    };

    const handlePrevClick = () => {
        if (swiperRef.current) swiperRef.current?.slidePrev();
    };

    const handleNextClick = () => {
        if (swiperRef.current) swiperRef.current?.slideNext();
    };

    const mapVideo = (el, idx) => {
        if (!el) return null;
        const link = Common.getVideoPageUrl(el);
        const thumbUrl = el.asset?.thumbnailUrl || "";
        const alt = el.title || "Video thumbnail";
        const activeClass = hoveredIdx === idx ? "tvp-hovered" : "tvp-normal";

        return (
            <Fragment key={idx}>
                <a
                    className={activeClass}
                    href={link}
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    onTouchStart={() => setHoveredIdx(idx)}
                    onTouchEnd={() => setHoveredIdx(null)}
                >
                    <div className="tvp-video-item-thumbnail">
                        <img src={thumbUrl} alt={alt} />
                        <div className="tvp-video-item-overlay">
                            <div className="tvp-video-item-overlay-content">
                                <svg
                                    className="tvp-play-icon-svg"
                                    viewBox="0 0 200 200"
                                >
                                    <polygon
                                        fill="#FFFFFF"
                                        points="70, 55 70, 145 145, 100"
                                    />
                                </svg>
                                <span className="tvp-hidden tvp-watch-now">
                                    WATCH NOW
                                </span>
                            </div>
                            <span className="tvp-video-duration pull-right">
                                {el.asset.prettyDuration}
                            </span>
                        </div>
                    </div>
                    <h2 className="tvp-video-title">
                        <Dotdotdot clamp={2}>{el.title}</Dotdotdot>
                    </h2>
                </a>
            </Fragment>
        );
    };

    const videos = channel?.videos || [];
    const isLoading = !videos.length;
    const slides = isLoading ? Array(4).fill(null) : videos;

    return (
        <div className="col-md-12" data-testid={`creatable-slider-${channel.id}`}>
            <div
                data-id={channel.id}
                ref={containerRef}
                className="tvp-slider-item"
            >
                <div className="tvp-slider-item-title">
                    {isLoading ? (
                        <div className="tvp-skeleton-title"></div>
                    ) : (<span>{channel.title}</span>)}
                </div>
                {!isLoading && (
                    <>
                        <div ref={prevRef} onClick={handlePrevClick}>
                            <PrevArrow />
                        </div>
                        <div ref={nextRef} onClick={handleNextClick}>
                            <NextArrow />
                        </div>
                    </>
                )}
                <Swiper
                    modules={[Navigation]}
                    spaceBetween={16}
                    slidesPerView={1}
                    breakpoints={{
                        768: { slidesPerView: 4, navigation: true },
                    }}
                    onSwiper={onSwiper}
                >
                    {slides.map((el, idx) => (
                        <SwiperSlide key={idx}>
                            {el ? (
                                mapVideo(el, idx)
                            ) : (
                                <div className="tvp-skeleton-slide">
                                    <div className="tvp-skeleton-thumbnail"></div>
                                    <div className="tvp-skeleton-text"></div>
                                </div>
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};

export default ChannelSliderItem;
